import { Observable, of, BehaviorSubject } from 'rxjs';
import { DatabaseRecord, DatabaseModel, DatabaseRecordIdentifier } from '../models/database-record.class';
import { DatabaseQuery } from './database.service';

export enum OfflineReason {
  ONLINE = 'Currently online.',
  FORCED = 'User manually set to offline mode.',
  BAD_NETWORK = 'Bad network connectivity to Fitforce server.',
  NO_NETWORK = 'The device is unable to access the Internet.',
}

export abstract class OfflineService {
  private statusSubject = new BehaviorSubject<boolean>(navigator.onLine);
  private online: boolean;
  private offlineForced = false;

  offlineReason: OfflineReason;
  status = this.statusSubject.asObservable();

  constructor() {
    this.online = navigator.onLine;
    this.setOfflineStatusReason();
    window.addEventListener('online', this.updateStatus.bind(this));
    window.addEventListener('offline', this.updateStatus.bind(this));
  }

  public isOnline(): boolean {
    return this.online && !this.offlineForced;
  }

  public isOffline(): boolean {
    return !this.online || this.offlineForced;
  }

  public isNavigatorOnline() {
    return navigator.onLine;
  }

  public isOfflineForced(): boolean {
    return this.offlineForced;
  }

  public forceOffline(setOffline: boolean, reason?: OfflineReason) {
    // Do not allow user to get online if navigator is offline
    if (!setOffline && !navigator.onLine) {
      return;
    }

    this.offlineForced = setOffline;
    this.online = !setOffline;

    if (this.online) {
      reason = OfflineReason.ONLINE;
    }
    this.offlineReason = reason;

    this.statusSubject.next(this.online);
  }

  /**
   * Implement in child class
   */
  public getRecord<T extends DatabaseRecord>(model: DatabaseModel, where: DatabaseRecordIdentifier | DatabaseQuery): Observable<T> {
    return of(null);
  }

  /**
   * Implement in child class
   */
  public getAll<T extends DatabaseRecord>(model: DatabaseModel): Observable<T[]> {
    return of(null);
  }

  /**
   * Implement in child class
   */
  public clear(model: DatabaseModel): Observable<void> {
    return of(null);
  }

  /**
   * Implement in child class
   */
  public query<T extends DatabaseRecord>(model: DatabaseModel, where: DatabaseQuery): Observable<T[]> {
    return of(null);
  }

  /**
   * Implement in child class
   */
  public queryUndeleted<T extends DatabaseRecord>(model: DatabaseModel, where: DatabaseQuery): Observable<T[]> {
    return of(null);
  }

  /**
   * Implement in child class
   */
  public save(model: DatabaseModel, records: DatabaseRecord | DatabaseRecord[]): Observable<void> {
    return of(null);
  }

  /**
   * Implement in child class
   */
  public delete(model: DatabaseModel, record: DatabaseRecord): Observable<void> {
    return of(null);
  }

  /**
   * Implement in child class
   */
  public deleteByKey(model: DatabaseModel, key: string): Observable<void> {
    return of(null);
  }

  /**
   * Implement in child class
   */
  public count(model: DatabaseModel): Observable<number> {
    return of(null);
  }

  private updateStatus() {
    if (!this.offlineForced && !navigator.onLine) {
      this.online = navigator.onLine;
    }
    this.setOfflineStatusReason();
    this.statusSubject.next(this.online);
  }

  private setOfflineStatusReason() {
    if (this.online) {
      this.offlineReason = OfflineReason.ONLINE;
    } else if (this.offlineForced) {
      this.offlineReason = OfflineReason.FORCED;
    } else {
      this.offlineReason = OfflineReason.NO_NETWORK;
    }
  }
}
